import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useAuth } from "@/context/AuthContext";

// Normalise legal content (plain text OR editor HTML) into well-structured HTML:
// numbered / ALL-CAPS lines become headings, "- " lines become bullet lists.
function legalToHtml(raw = "") {
  if (!raw) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const hasTags = /<(p|br|ul|ol|li|h[1-6]|strong|em|b|i|u|div|table)\b/i.test(raw);
  let html;
  if (hasTags) {
    html = raw;
  } else {
    const parts = [];
    let li = [];
    const flush = () => { if (li.length) { parts.push(`<ul>${li.join("")}</ul>`); li = []; } };
    for (const line of raw.replace(/\r/g, "").split("\n")) {
      const t = line.trim();
      if (!t) { flush(); continue; }
      if (/^[-•]\s+/.test(t)) { li.push(`<li>${esc(t.replace(/^[-•]\s+/, ""))}</li>`); continue; }
      flush();
      if (/^\d+\.\s+\S/.test(t) && t.length <= 80) parts.push(`<h2>${esc(t)}</h2>`);
      else if (/^[A-Z0-9][A-Z0-9 &/,'"().-]{2,70}$/.test(t)) parts.push(`<h2>${esc(t)}</h2>`);
      else parts.push(`<p>${esc(t)}</p>`);
    }
    flush();
    html = parts.join("");
  }
  // Strip dangerous markup even from admin-authored HTML (defense-in-depth against stored XSS).
  html = html
    .replace(/<\s*(script|style|iframe|object|embed)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|style|iframe|object|embed)\b[^>]*\/?>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(href|src)\s*=\s*(["'])?\s*javascript:[^"'>\s]*/gi, '$1="#"');
  // Upgrade heading-like paragraphs (numbered or ALL-CAPS) that arrived as <p>.
  return html
    .replace(/<p>\s*(\d+\.\s+[^<]{1,80})<\/p>/gi, "<h2>$1</h2>")
    .replace(/<p>\s*([A-Z0-9][A-Z0-9 &/,'"().-]{2,70})<\/p>/g, "<h2>$1</h2>");
}

const LEGAL_PROSE =
  "mt-8 leading-relaxed text-slate-700 text-justify " +
  "[&_p]:mb-4 " +
  "[&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-blue-700 " +
  "[&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-blue-700 [&_h2]:tracking-tight [&_h2]:text-left " +
  "[&_h3]:mt-6 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900 " +
  "[&_h4]:mt-4 [&_h4]:mb-1 [&_h4]:font-semibold [&_h4]:text-slate-900 " +
  "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 " +
  "[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 " +
  "[&_strong]:font-semibold [&_strong]:text-slate-900 [&_b]:font-semibold [&_b]:text-slate-900 " +
  "[&_a]:text-blue-600 [&_a]:underline";

export default function LegalPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageMeta(page ? `${page.title} · Suffle Online` : "Legal · Suffle Online", page ? `Read the ${page.title} for Suffle Online.` : "Suffle Online legal and policy documents.");
  useEffect(() => {
    api.get(`/legal/${slug}`).then((r) => setPage(r.data.page)).catch(() => setNotFound(true));
    // SEO canonical for public policy pages (indexable, no duplicate URLs)
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `https://www.suffleonline.com/legal/${slug}`;
    return () => { const l = document.querySelector('link[rel="canonical"]'); if (l) l.remove(); };
  }, [slug]);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/"><Logo variant="lg" /></Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {notFound ? (
          <p className="text-slate-500">Page not found.</p>
        ) : !page ? (
          <div className="h-64 animate-pulse rounded-2xl bg-slate-50" />
        ) : (
          <article>
            <h1 className="font-brand text-3xl font-bold text-slate-900">{page.title}</h1>
            {user?.role === "super_admin" && (
              <span data-testid="legal-freshness-badge" className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${page.review_status === "legally_approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {page.review_status === "legally_approved" ? `Legally approved${page.legally_approved_by ? ` · ${page.legally_approved_by}` : ""}` : "Draft — pending legal review"}
              </span>
            )}
            <p className="mt-2 text-sm text-slate-400">Version {page.version} · Effective {page.effective_date}</p>
            <div data-testid="legal-content" className={LEGAL_PROSE} style={{ hyphens: "auto" }} dangerouslySetInnerHTML={{ __html: legalToHtml(page.content) }} />
          </article>
        )}
      </main>
    </div>
  );
}
